import { XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import type { Notification } from '../api/notifications.api';
import NotificationActionMenu from './NotificationActionMenu';

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  unreadCount: number;
  onMarkAsRead: (notificationId: string) => void;
  onDelete: (notificationId: string) => void;
  isDeleting?: boolean;
  notificationRef?: React.RefObject<HTMLDivElement>;
}

export default function NotificationDrawer({
  isOpen,
  onClose,
  notifications,
  unreadCount,
  onMarkAsRead,
  onDelete,
  isDeleting = false,
  notificationRef
}: NotificationDrawerProps) {
  if (!isOpen) return null;

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp)
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
      .replace(',', '');
  };

  return (
    <>
      {/* Desktop Notification Popup */}
      <div className="fixed inset-0 z-50 hidden lg:block">
        {/* Backdrop (outside click closes) */}
        <div
          className="absolute inset-0 bg-black/10"
          onClick={onClose}
          aria-hidden="true"
        />

        <div
          ref={notificationRef}
          className="absolute right-4 top-12 w-96 rounded-lg border border-gray-200 bg-white shadow-xl"
        >
          <div className="border-b border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                NOTIFICATION ({unreadCount})
              </h3>
              <div className="flex items-center gap-4">
                <Link
                  to="/notifications"
                  className="text-sm font-medium text-orange-600 hover:text-orange-700"
                  onClick={onClose}
                >
                  View More
                </Link>
                <button
                  type="button"
                  className="rounded-full p-1 text-gray-400 hover:text-gray-600"
                  onClick={onClose}
                  aria-label="Close notifications"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.map((notification) => (
              <div
                key={notification.notifications_id}
                className={`cursor-pointer border-b border-gray-100 p-4 hover:bg-gray-50 ${
                  notification.status === 'unread' ? 'bg-orange-50' : ''
                }`}
                onClick={() => onMarkAsRead(notification.notifications_id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="mb-2 text-sm text-gray-600">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatTimestamp(notification.timestamp)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {notification.status === 'unread' && (
                      <div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-orange-500"></div>
                    )}
                    <div onClick={(e) => e.stopPropagation()}>
                      <NotificationActionMenu
                        notificationId={notification.notifications_id}
                        onDelete={onDelete}
                        isDeleting={isDeleting}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Notification Drawer */}
      <div className="fixed inset-0 z-50 lg:hidden">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />

        {/* Drawer */}
        <div
          ref={notificationRef}
          className="fixed bottom-0 left-0 right-0 flex h-[80vh] transform flex-col rounded-t-2xl bg-white shadow-2xl transition-transform duration-300 ease-out"
        >
          {/* Drawer Handle */}
          <div className="flex justify-center pb-2 pt-3">
            <div className="h-1 w-12 rounded-full bg-gray-300"></div>
          </div>

          {/* Drawer Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h3 className="text-base text-gray-900">NOTIFICATIONS</h3>
              </div>
              <button
                type="button"
                className="rounded-full p-2 text-gray-400 hover:text-gray-600"
                onClick={onClose}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {notifications.map((notification) => (
              <div
                key={notification.notifications_id}
                className={`flex cursor-pointer items-start space-x-3 border-b border-gray-100 px-2 py-4 last:border-b-0 ${
                  notification.status === 'unread' ? 'bg-orange-50' : ''
                }`}
                onClick={() => onMarkAsRead(notification.notifications_id)}
              >
                {notification.status === 'unread' && (
                  <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-red-500"></div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900">
                    {notification.message}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatTimestamp(notification.timestamp)}
                  </p>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <NotificationActionMenu
                    notificationId={notification.notifications_id}
                    onDelete={onDelete}
                    isDeleting={isDeleting}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Drawer Footer */}
          <div className="border-t border-gray-200 px-6 py-4">
            <Link
              to="/notifications"
              className="block w-full rounded-lg bg-orange-600 px-4 py-2 text-center text-sm font-medium text-white hover:bg-orange-700"
              onClick={onClose}
            >
              View All Notifications
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
